import React, { useEffect, useRef, useState } from 'react';
import { ModalPortal } from './ModalPortal';
import { carteraApi } from './api';
import { prodCorto } from './helpers';

/*
 * Captura o CORRECCIÓN del teléfono de un contrato.
 *
 * Sale encima del hilo (blur de fondo) porque sin número no hay nada que hacer
 * en esa conversación: no se puede enviar plantilla ni texto. Va por POST
 * /cartera/{credito}/telefono — solo guarda, NO envía.
 *
 * El número queda en la CUENTA del cliente (users.phone, cifrado), no en el
 * contrato: por eso capturarlo una vez sirve para todos sus contratos, y por
 * eso puede fundir dos tarjetas de la bandeja en una.
 *
 * Regla del proyecto "validación visible, no botón muerto": si el campo está
 * vacío o el número es inválido, se marca en rojo con el motivo y se enfoca;
 * nunca se deja el botón deshabilitado sin decir qué falta.
 */

// El +52 va PINTADO en el campo, no se escribe: en el input solo van los 10
// dígitos nacionales. Toda la cartera es México, y la lada a mano era una
// fuente de errores tonta — pegar el número con lada y que quedara duplicada
// (5252…), o escribir sobre el valor precargado y concatenarlo.
//
// ⚠️ Esto deja el modal SIN forma de capturar un número de otro país. Hoy no
// hay ninguno (los 38 contratos son 52), pero si algún día hace falta, aquí es
// donde hay que abrir la puerta — no en la validación del backend, que sigue
// aceptando de 10 a 15 dígitos.
const LADA_MX = '52';

// Deja los 10 dígitos nacionales. Aguanta que peguen el número completo
// (525583539764, +52 55 8353 9764, 5215583539764) y le quita la lada. Solo
// recorta cuando SOBRAN dígitos, para no mutilar lo que se está tecleando:
// un 5512345678 legítimo de CDMX empieza con 52 y debe quedarse entero.
const soloNacional = (valor) => {
  let digitos = String(valor ?? '').replace(/\D+/g, '');
  if (digitos.length > 10 && digitos.startsWith('521')) digitos = digitos.slice(3);
  else if (digitos.length > 10 && digitos.startsWith('52')) digitos = digitos.slice(2);

  return digitos.slice(0, 10);
};

export const CapturarTelefonoModal = ({ credito, cliente, onClose, onGuardado }) => {
  // Si el contrato ya tiene número, el modal entra en modo "corregir": precarga
  // el actual y cambia los textos. Mismo endpoint, mismas validaciones.
  //
  // `completo` es lo que se le enseña al usuario en la frase de arriba (E.164,
  // tal cual viaja a Meta); `actual` es la parte que va DENTRO del input.
  const completo = credito?.telefono ? String(credito.telefono) : '';
  const editando = completo !== '';
  const actual = soloNacional(completo);
  const [telefono, setTelefono] = useState(actual);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const guardar = async () => {
    const digitos = telefono.replace(/\D+/g, '');
    if (!digitos) {
      setError('Escribe el teléfono para poder guardarlo.');
      inputRef.current?.focus();
      return;
    }
    // Con el +52 fijo enfrente, lo que falta son exactamente los 10 nacionales.
    if (digitos.length !== 10) {
      setError(`Faltan dígitos: son 10, sin el ${LADA_MX} — ese ya va enfrente.`);
      inputRef.current?.focus();
      return;
    }

    setGuardando(true);
    setError(null);
    try {
      const res = await carteraApi.guardarTelefonoCredito(credito.id, `${LADA_MX}${digitos}`);
      // El id va de vuelta para que la bandeja pueda reengancharse a ESTE
      // contrato: guardar el teléfono puede fundir su tarjeta con otra y
      // dejar la selección huérfana (ver anclaCreditoRef).
      onGuardado?.(res?.data?.data?.telefono || digitos, credito.id);
    } catch (e) {
      const validacion = e?.response?.data?.errors?.telefono?.[0];
      setError(validacion || e?.response?.data?.message || 'No se pudo guardar el teléfono.');
      inputRef.current?.focus();
    } finally {
      setGuardando(false);
    }
  };

  return (
    <ModalPortal>
      <div
        className="cart-modal-overlay cm-blur"
        onMouseDown={(e) => { if (e.target === e.currentTarget && !guardando) onClose(); }}
      >
        <div className="cart-modal" style={{ maxWidth: 420 }}>
          <div className="cm-head">
            <div style={{
              width: 38, height: 38, borderRadius: 11, background: '#FFF6E5', color: '#9A681D',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <span className="pi pi-phone" style={{ fontSize: 15 }} />
            </div>
            <h3>{editando ? 'Cambiar el teléfono' : 'Falta el teléfono'}</h3>
            <button type="button" className="cm-close" onClick={onClose} disabled={guardando}>
              <span className="pi pi-times" />
            </button>
          </div>

          <div className="cm-credit">
            {editando
              ? <>El número actual de <b>{cliente}</b> es <b className="tnum">+{completo}</b>. Al guardar, los mensajes nuevos irán al número corregido.</>
              : <>No se puede abrir conversación con <b>{cliente}</b> hasta capturar su número.</>}
            {credito?.clave_contrato && (
              <> Contrato <b className="tnum">{prodCorto(credito.tipo)} · {credito.clave_contrato}</b>.</>
            )}
          </div>

          <div className={`cm-field${error ? ' cm-field-err' : ''}`}>
            <label htmlFor="cm-tel">Teléfono</label>
            {/* El +52 es parte del campo, no del texto que se teclea. El borde
                vive en el contenedor para que se lean como un solo control. */}
            <div className={`cm-tel${error ? ' cm-tel-err' : ''}`}>
              <span className="cm-tel-lada" aria-hidden="true">🇲🇽 +{LADA_MX}</span>
              <input
                id="cm-tel"
                ref={inputRef}
                className="cm-input tnum"
                value={telefono}
                inputMode="numeric"
                autoComplete="tel-national"
                /* Sin maxLength: recortaría el pegado ANTES de que soloNacional
                   pueda quitarle la lada, y 525583539764 quedaría en 5255835397
                   —un número que no existe—. El tope de 10 lo pone el
                   normalizador, que primero decide qué sobra. */
                placeholder="55 1234 5678"
                aria-label={`Teléfono de 10 dígitos, lada de México +${LADA_MX}`}
                onChange={(e) => { setTelefono(soloNacional(e.target.value)); if (error) setError(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') guardar(); }}
              />
            </div>
            {error ? (
              <div className="cm-hint-err">
                <span className="pi pi-exclamation-circle" />{error}
              </div>
            ) : (
              <div style={{ fontSize: 11.5, color: '#7A8896', marginTop: 5 }}>
                Solo los 10 dígitos. Queda en la cuenta del cliente, así que sirve
                para todos sus contratos.
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 9, marginTop: 18 }}>
            <button
              type="button"
              className="btn"
              onClick={onClose}
              disabled={guardando}
              style={{ flex: 1, background: '#F1F5F8', color: '#475569', border: 'none', borderRadius: 12, padding: '10px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
            >
              Ahora no
            </button>
            <button
              type="button"
              className="btn"
              onClick={guardar}
              disabled={guardando}
              style={{ flex: 1.4, background: '#22EC9A', color: '#06241a', border: 'none', borderRadius: 12, padding: '10px 14px', fontWeight: 700, fontSize: 13, cursor: guardando ? 'not-allowed' : 'pointer' }}
            >
              <span className={`pi ${guardando ? 'pi-spin pi-spinner' : 'pi-check'}`} style={{ marginRight: 6, fontSize: 12 }} />
              {guardando ? 'Guardando…' : (editando ? 'Actualizar teléfono' : 'Guardar teléfono')}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default CapturarTelefonoModal;
